import React, { useCallback, useMemo } from 'react';
import { GroupElement } from '@bynder-studio/render-web';
import { type VisualElement } from '@bynder-studio/render-core';
import { Tooltip, Divider, Dropdown } from '@bynder/design-system';
import { IconCopy, IconCreateGroup, IconDelete, IconMore, IconUngroup, IconStarredFolder } from '@bynder/icons';
import useEditor from '~/hooks/useEditor';
import AddLayerButton from '~/common/editor/layers/addLayerButton/AddLayerButton';
import permissions from '~/configs/permissions';
import AuthorizationHelper from '~/helpers/AuthorizationHelper';
import { collectTopLevelElements } from '~/common/editor/timeline/timeline-actions/components/utils';
import usePresets from '~/common/editor/presets/usePresets';
import {
    DELETE_GROUP,
    DELETE_LAYER,
    DUPLICATE_LAYER,
    GROUP_LAYERS,
    LAYERS,
    MORE_OPTIONS,
    SAVE_AS_PRESET,
    UNGROUP,
} from '~/helpers/textConstants';
import { AddElementWrapper, LayerButton } from '../addLayerButton/AddLayerButton.styled';
import useElementGeneralActions from 'packages/pages/editor/useElementGeneralActions';
import useGeneralShortcuts from 'packages/hooks/shortcuts/useGeneralShortcuts';
import useForceUpdate from '~/hooks/useForceUpdate';
import usePresetShortcuts from 'packages/hooks/shortcuts/usePresetShortcut';
import { parseShortcutForTooltip, WithShortcutLabel } from 'packages/common/WithShortcutLabel';

const LayerHeader = ({ selectedGroups, setSelectedGroups }) => {
    const { createPreset } = usePresets();
    const { creativeModel, manipulationRenderer } = useEditor();
    const forceUpdate = useForceUpdate();

    const selectedElements = manipulationRenderer?.getSelectedElements() || [];
    const isGroup = useMemo(() => {
        const els = selectedElements.filter(
            (el) => !el.parent || selectedElements.findIndex((e) => e.id === el.parent.id) === -1,
        );

        return els.length === 1 && els.every((el) => el instanceof GroupElement);
    }, [selectedElements]);

    const { handleSelection, handleDelete, handleDuplicate, handleLock } = useElementGeneralActions(
        setSelectedGroups,
        selectedGroups,
    );

    const handleAddOrRemoveGroup = useCallback(() => {
        if (isGroup) {
            const elementToSelect = selectedGroups[selectedGroups.length - 1].children.reduce(
                (elA, elB) => ((elA?.renderOrder || 0) > (elB?.renderOrder || 0) ? elA : elB),
                null,
            );

            const editorElements = creativeModel.getAllElementsRecursively<VisualElement>('visual');
            const maskedElements = editorElements.filter((el) => el.mask && el.mask.elementId);

            selectedGroups.forEach((group) => {
                creativeModel.unwrapGroupElement(group.id);

                if (maskedElements) {
                    maskedElements.forEach((element) => {
                        if (group.id === element.mask.elementId) {
                            creativeModel.updateElement(element.id, {
                                mask: null,
                            });
                        }
                    });
                }

                if (group.mask && group.mask.elementId) {
                    const sameElementMaskedElements = maskedElements
                        .filter((el) => !el.id === group.id)
                        .filter((el) => el.mask.elementId === group.mask.elementId);

                    if (!sameElementMaskedElements.length) {
                        creativeModel.updateElement(group.mask.elementId, {
                            mask: null,
                        });
                    }
                }
            });

            if (elementToSelect) {
                manipulationRenderer.selectElement(elementToSelect.id);
            } else {
                setSelectedGroups([]);
            }
        } else if (selectedElements.length) {
            const groupElement = creativeModel.wrapElementsIntoGroup(selectedElements.map((el) => el.id));
            manipulationRenderer.selectElement(groupElement.id);
        }
    }, [isGroup, selectedElements, creativeModel, manipulationRenderer]);

    const handleCreatePreset = useCallback(() => {
        if (isGroup) {
            createPreset(selectedGroups[0]);
        }
    }, [createPreset, selectedGroups]);

    const handleHide = useCallback(() => {
        collectTopLevelElements(manipulationRenderer?.getSelectedElements()).forEach((el) => {
            creativeModel.updateElement(el.id, { hidden: !el.hidden });
        });
        forceUpdate();
    }, [creativeModel, manipulationRenderer]);

    useGeneralShortcuts({
        handleDuplicate,
        handleSelection,
        handleGroup: handleAddOrRemoveGroup,
        handleUnGroup: handleAddOrRemoveGroup,
        handleDelete,
        handleLock,
        handleHide,
    });
    usePresetShortcuts({ createPreset: handleCreatePreset });

    const showActionButtons = !!selectedElements.length;

    return (
        <div className="layers--header position-relative">
            <span>{LAYERS}</span>
            <div className="layers--header-action-btns d-flex align-items-center">
                {showActionButtons && (
                    <Tooltip
                        content={
                            !isGroup
                                ? `${GROUP_LAYERS} (${parseShortcutForTooltip(['cmd', 'G'])})`
                                : `${UNGROUP} (${parseShortcutForTooltip(['shift', 'cmd', 'G'])})`
                        }
                    >
                        <AddElementWrapper>
                            <LayerButton
                                title=""
                                variant="clean"
                                isDisabled={!selectedElements.length}
                                onClick={handleAddOrRemoveGroup}
                                icon={!isGroup ? <IconCreateGroup /> : <IconUngroup />}
                            />
                        </AddElementWrapper>
                    </Tooltip>
                )}
                {showActionButtons && (
                    <Tooltip content={`${DUPLICATE_LAYER} (${parseShortcutForTooltip(['cmd', 'D'])})`}>
                        <AddElementWrapper>
                            <LayerButton title="" variant="clean" onClick={handleDuplicate} icon={<IconCopy />} />
                        </AddElementWrapper>
                    </Tooltip>
                )}
                {showActionButtons && !isGroup && (
                    <Tooltip content={`${DELETE_LAYER} (${parseShortcutForTooltip(['backspace'])})`}>
                        <AddElementWrapper>
                            <LayerButton title="" variant="clean" onClick={handleDelete} icon={<IconDelete />} />
                        </AddElementWrapper>
                    </Tooltip>
                )}
                {showActionButtons && isGroup && (
                    <Dropdown
                        position="bottom-right"
                        trigger={({ isOpen, ...triggerProps }) => (
                            <Tooltip content={MORE_OPTIONS}>
                                <AddElementWrapper>
                                    <LayerButton
                                        title=""
                                        icon={<IconMore />}
                                        variant="clean"
                                        isDisabled={false}
                                        {...triggerProps}
                                    />
                                </AddElementWrapper>
                            </Tooltip>
                        )}
                    >
                        {AuthorizationHelper.hasPermission(permissions.PRESETS.WRITE) && (
                            <Dropdown.Item icon={<IconStarredFolder />} onClick={handleCreatePreset}>
                                <WithShortcutLabel shortcut={['cmd', 'P']}>{SAVE_AS_PRESET}</WithShortcutLabel>
                            </Dropdown.Item>
                        )}
                        <Divider />
                        <Dropdown.Item icon={<IconDelete />} onClick={handleDelete}>
                            <WithShortcutLabel shortcut={['backspace']}>{DELETE_GROUP}</WithShortcutLabel>
                        </Dropdown.Item>
                    </Dropdown>
                )}
                <AddLayerButton />
            </div>
        </div>
    );
};

export default LayerHeader;
